import { formatFiat, formatUnits } from '@shared/lib/format';
import {
  USDC_DECIMALS,
  type Card,
  type CardOperation,
  type CardOperationStatus,
  type CardTransaction,
} from './card.types';

const KNOWN_FAILURE_REASONS = [
  'FUNDING_NOT_SIGNED',
  'FUNDING_PREPARE_FAILED',
  'FUNDING_PREPARE_ABANDONED',
  'FUNDING_FAILED',
  'FUNDING_REVERTED',
  'FUNDING_AMOUNT_MISMATCH',
  'ISSUE_FAILED',
  'ISSUE_PARAMS_MISSING',
  'TOPUP_PARAMS_MISSING',
  'CARD_MISSING',
  'PROVIDER_TIMEOUT',
  'CARD_CLOSED_BALANCE_RETURNED',
];

const REFUNDING_FAILURE_PREFIXES = ['PROVIDER_', 'CARD_PROVIDER_', 'BRIDGE_'];

const INTENT_FAILURE_KEYS: Record<string, string> = {
  ONCHAIN_REVERT: 'common.txFailure.onchainRevert',
  RELAY_FAILED: 'common.txFailure.relayFailed',
  RELAY_TIMEOUT: 'common.txFailure.relayTimeout',
  TX_DROPPED: 'common.txFailure.txDropped',
  EXPIRED: 'common.txFailure.expired',
  TRON_TX_EXPIRED: 'common.txFailure.expired',
};

export function formatUsdc(amountRaw: bigint): string {
  return formatUnits(amountRaw, USDC_DECIMALS);
}

export function cardBalanceDisplay(card: Card): string {
  if (card.status === 'PENDING') return '—';
  return formatFiat(card.balanceMinor, card.currencyCode, card.currencyPrecision);
}

export function transactionAmountDisplay(tx: CardTransaction): string {
  const magnitude = tx.amountMinor < 0n ? -tx.amountMinor : tx.amountMinor;
  const formatted = formatFiat(magnitude, tx.currencyCode, tx.currencyPrecision);
  if (tx.direction === 'incoming') return `+${formatted}`;
  if (tx.direction === 'outgoing') return `−${formatted}`;
  return formatted;
}

export function operationStatusKey(status: CardOperationStatus): string {
  return `cards.ops.status.${status}`;
}

export function failureReasonKey(reason: string | null): string {
  if (!reason) return 'cards.failure.generic';
  if (KNOWN_FAILURE_REASONS.includes(reason)) return `cards.failure.${reason}`;
  const intentKey = INTENT_FAILURE_KEYS[reason];
  if (intentKey) return intentKey;
  if (REFUNDING_FAILURE_PREFIXES.some(prefix => reason.startsWith(prefix))) {
    return 'cards.failure.providerRefund';
  }
  return 'cards.failure.generic';
}

export function operationLabelKey(operation: CardOperation): string {
  switch (operation.type) {
    case 'ISSUE':
      return 'cards.op.labelIssue';
    case 'PAYOUT':
      return 'cards.op.labelPayout';
    default:
      return 'cards.op.labelTopup';
  }
}

export function minutesSince(iso: string | null): number | null {
  if (!iso) return null;
  const diff = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(diff) || diff < 0) return null;
  return Math.floor(diff / 60000);
}

export function isStale(card: Card, thresholdMinutes = 15): boolean {
  const minutes = minutesSince(card.balanceSyncedAt);
  return minutes !== null && minutes >= thresholdMinutes;
}
